/**
 * Profile merge utility for web-app Profile objects.
 * Union strategy: combine all data, deduplicate, prefer the richer value.
 */

import type { Profile } from '@/store/profile.store';

function dedup(a: string[], b: string[]): string[] {
  const set = new Set([...a, ...b].map((s) => s.trim()).filter(Boolean));
  return [...set];
}

function deduplicateByKey<T>(
  a: T[],
  b: T[],
  keyFn: (item: T) => string,
): T[] {
  const seen = new Map<string, T>();
  for (const item of [...a, ...b]) {
    const key = keyFn(item).toLowerCase().trim();
    if (!seen.has(key)) {
      seen.set(key, item);
    }
  }
  return [...seen.values()];
}

export function mergeProfiles(local: Profile, server: Profile): Profile {
  return {
    id: server.id || local.id,
    userId: server.userId || local.userId,
    firstName: local.firstName || server.firstName,
    lastName: local.lastName || server.lastName,
    email: local.email || server.email,
    phone: local.phone || server.phone,
    location: local.location || server.location,
    summary:
      (server.summary || '').length > (local.summary || '').length
        ? server.summary
        : local.summary || server.summary,
    skills: dedup(local.skills || [], server.skills || []),
    languages: dedup(local.languages || [], server.languages || []),
    experience: deduplicateByKey(
      local.experience || [],
      server.experience || [],
      (e: any) => `${e.company}-${e.position || e.title}-${e.startDate}`,
    ),
    education: deduplicateByKey(
      local.education || [],
      server.education || [],
      (e) => `${e.institution}-${e.degree}`,
    ),
    certifications: deduplicateByKey(
      local.certifications || [],
      server.certifications || [],
      (c) => `${c.name}-${c.issuer}`,
    ),
    // ATS score: keep the most recent
    lastATSScore: pickLatestScore(local, server).score,
    lastATSAnalyzedAt: pickLatestScore(local, server).at,
    createdAt: local.createdAt < server.createdAt ? local.createdAt : server.createdAt,
    updatedAt: new Date(),
  };
}

function pickLatestScore(
  a: Profile,
  b: Profile,
): { score?: number; at?: string } {
  if (a.lastATSScore == null && b.lastATSScore == null) return {};
  if (a.lastATSScore != null && b.lastATSScore == null)
    return { score: a.lastATSScore, at: a.lastATSAnalyzedAt };
  if (b.lastATSScore != null && a.lastATSScore == null)
    return { score: b.lastATSScore, at: b.lastATSAnalyzedAt };
  const aDate = a.lastATSAnalyzedAt ? new Date(a.lastATSAnalyzedAt).getTime() : 0;
  const bDate = b.lastATSAnalyzedAt ? new Date(b.lastATSAnalyzedAt).getTime() : 0;
  return bDate > aDate
    ? { score: b.lastATSScore, at: b.lastATSAnalyzedAt }
    : { score: a.lastATSScore, at: a.lastATSAnalyzedAt };
}
